import React, {
    FC,
    useMemo,
    useRef,
    useCallback,
    useEffect,
    useState
} from "react"
import { TagsInputDropdown } from "../AddData/TagsInputDropdown"
import { useTagList } from "../../../../../../Hooks/useTagList"
import { useBoolean } from "../../../../../../Hooks/useBoolean"
import { IconEdit, Chip } from "dash-styled"
import { ReactComponent as IconTag } from "icons/lib/tag.svg"
import { NavLink } from "react-router-dom"

interface TagsViewEditProps {
    appID: string
    value: string[]
    className?: string
    rightAlign?: boolean
    linkFactory?: (tag: string) => string
    onAdd?: (value: string, isNew: boolean) => void
    onRemove?: (value: string) => void
}

export const TagsViewEdit: FC<TagsViewEditProps> = ({
    appID,
    value,
    className,
    rightAlign = false,
    linkFactory,
    onAdd,
    onRemove
}) => {
    const [tagSearchKeyword, setTagSearchKeyword] = useState("")
    const tagsFetch = useTagList(appID, tagSearchKeyword)
    const editorRef = useRef<Pick<HTMLInputElement, "focus" | "contains">>(null)
    const [isEditing, startEdit, endEdit] = useBoolean(false)
    const [isEditButtonShown, showEditButton, hideEditButton] =
        useBoolean(false)
    const sortedValue = useMemo(() => [...value].sort(), [value])
    const handleClickEdit = useCallback(() => {
        startEdit()
        hideEditButton()
        window.requestAnimationFrame(() => editorRef.current?.focus())
    }, [startEdit, hideEditButton])

    useEffect(() => {
        if (!isEditing) {
            return
        }

        const clickHandler = (e: MouseEvent) => {
            if (
                e.target instanceof HTMLElement &&
                editorRef.current !== null &&
                !editorRef.current.contains(e.target)
            ) {
                endEdit()
            }
        }

        document.addEventListener("click", clickHandler)

        return () => {
            document.removeEventListener("click", clickHandler)
        }
    }, [endEdit, isEditing])

    const tags = sortedValue.map(t => (
        <Chip
            testId="job-tag"
            size="sm"
            key={t}
            className="mr-1.5 mb-1"
            labelClassname="text-rhino-800"
            linkTo={linkFactory?.(t)}
            linkComponent={NavLink}
        >
            {t}
        </Chip>
    ))
    const ignore = () => {}
    const placeholderChip =
        onAdd === undefined || onRemove === undefined ? null : (
            <Chip
                presentation={true}
                className="max-w-0 w-0 invisible mb-1"
                size="sm"
                onRemove={ignore}
            >
                placeholder
            </Chip>
        ) // invisible placeholder to keep equal height

    return isEditing && onAdd !== undefined && onRemove !== undefined ? (
        <TagsInputDropdown
            testId="edit-tags-input"
            ref={editorRef}
            rightAlign={rightAlign}
            allowAddNewTags={true}
            noPlaceholder={true}
            className={className}
            tagsFetch={tagsFetch}
            value={sortedValue}
            onAdd={onAdd}
            onRemove={onRemove}
            onSearch={setTagSearchKeyword}
        />
    ) : (
        <div
            className={
                `box-content border border-solid border-transparent pt-2 pb-1 flex items-center flex-wrap ${
                    className ?? ""
                }` /* transparent border to make height exactly same as tag input */
            }
            onMouseEnter={showEditButton}
            onMouseLeave={hideEditButton}
        >
            {rightAlign ? placeholderChip : tags}
            {onAdd === undefined || onRemove === undefined ? null : (
                <button
                    data-testid="edit-tags-button"
                    className={`${
                        isEditButtonShown || value.length === 0
                            ? ""
                            : "opacity-0"
                    } leading-none p-0 text-marketplace-pill mr-1.5 mb-1 flex items-center whitespace-no-wrap`}
                    onClick={handleClickEdit}
                    onFocus={showEditButton}
                    onBlur={hideEditButton}
                >
                    {value.length > 0 ? (
                        <IconEdit className="flex-shrink-0" />
                    ) : (
                        <>
                            <IconTag className="mr-1 flex-shrink-0" />
                            Add tags
                        </>
                    )}
                </button>
            )}
            {rightAlign ? tags : placeholderChip}
        </div>
    )
}
